import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Loader2, Trash2, Undo2, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import DatePicker from '@/components/DatePicker';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INDUSTRIES = [
    'Énergie', 'Santé', 'Agriculture', 'Infrastructure', 'Services', 'Mines', 'ONG / Associatif',
    'Éducation', 'Eau & Assainissement', 'Logistique & Transport', 'Immobilier & Construction',
    'Fintech & Inclusion financière', 'Industrie & Transformation locale', 'Smart Cities & Territoires',
];
const STATUS_LABEL = { draft: 'Brouillon', active: 'Actif', on_hold: 'En attente', completed: 'Terminé', cancelled: 'Annulé' };

// (V15) Sector mapping + phase seeding now happens server-side in
// POST /api/projects/create — see /app/backend/server.py:project_create.

export default function Projects() {
    const { t } = useLang();
    const { session, profile } = useAuth();
    const { currentWorkspace, refreshAccount } = useWorkspace();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState('active');
    const [projects, setProjects] = useState([]);
    const [trash, setTrash] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingTrash, setLoadingTrash] = useState(false);
    const [open, setOpen] = useState(searchParams.get('new') === '1');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        name: '', code: '', industry: INDUSTRIES[0], country: '',
        budget_total: '', currency: 'XOF', start_date: '', end_date: '', description: '',
    });

    const canManageTrash = currentWorkspace?.is_admin || profile?.is_super_admin;
    // Only workspace admins (or super admins) can create projects. Contributors
    // can collaborate on existing ones but cannot spawn new projects.
    const canCreateProject = currentWorkspace?.is_admin || profile?.is_super_admin;

    const fetchProjects = useCallback(async () => {
        if (!currentWorkspace) return;
        setLoading(true);
        const { data } = await supabase
            .from('projects')
            .select('id,name,code,industry,status,progress,budget_total,currency,created_at,deleted_at')
            .eq('workspace_id', currentWorkspace.id)
            .is('deleted_at', null)
            .order('created_at', { ascending: false });
        setProjects(data || []);
        setLoading(false);
    }, [currentWorkspace]);

    const fetchTrash = useCallback(async () => {
        if (!currentWorkspace || !session) return;
        setLoadingTrash(true);
        try {
            const r = await axios.get(`${API}/projects/trash`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                params: { workspace_id: currentWorkspace.id },
            });
            setTrash(r.data.items || []);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setLoadingTrash(false);
    }, [currentWorkspace, session]);

    useEffect(() => { fetchProjects(); }, [fetchProjects]);
    useEffect(() => { if (tab === 'trash') fetchTrash(); }, [tab, fetchTrash]);

    const restoreProject = async (p) => {
        try {
            await axios.post(`${API}/projects/${p.id}/restore`, {}, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                params: { workspace_id: currentWorkspace.id },
            });
            toast.success(`« ${p.name} » restauré`);
            await Promise.all([fetchProjects(), fetchTrash()]);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const purgeProject = async (p) => {
        // eslint-disable-next-line no-alert
        const txt = window.prompt(`Pour SUPPRIMER DÉFINITIVEMENT « ${p.name} » et toutes ses données (factures, dépenses, GED, réunions…), saisissez exactement le nom du projet :`);
        if (txt == null) return;
        try {
            await axios.delete(`${API}/projects/${p.id}`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                params: { workspace_id: currentWorkspace.id, confirm_name: txt, hard: true },
            });
            toast.success('Projet supprimé définitivement');
            await fetchTrash();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const create = async (e) => {
        e.preventDefault();
        if (!currentWorkspace) return;
        setCreating(true); setError(null);
        const payload = {
            workspace_id: currentWorkspace.id,
            name: form.name,
            code: form.code || null,
            industry: form.industry,
            country: form.country || null,
            budget_total: form.budget_total ? Number(form.budget_total) : 0,
            currency: form.currency || 'XOF',
            start_date: form.start_date || null,
            end_date: form.end_date || null,
            description: form.description || null,
        };
        try {
            await axios.post(`${API}/projects/create`, payload, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            setCreating(false);
            setOpen(false); setSearchParams({});
            setForm({ name: '', code: '', industry: INDUSTRIES[0], country: '', budget_total: '', currency: 'XOF', start_date: '', end_date: '', description: '' });
            toast.success('Projet créé');
            fetchProjects();
            if (refreshAccount) refreshAccount();
        } catch (err) {
            const msg = err.response?.data?.detail || err.message;
            setError(msg);
            toast.error(msg);
            setCreating(false);
        }
    };

    if (!currentWorkspace) {
        return <div className="text-center text-foreground/60 py-20">{t('common.no_workspace')}</div>;
    }

    return (
        <div className="space-y-8">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow mb-2">Portefeuille</div>
                    <h1 className="font-heading text-4xl">{t('projects.list_title')}</h1>
                </div>
                <Button onClick={() => setOpen(true)} disabled={!canCreateProject} className="bg-primary hover:bg-primary-hover text-primary-foreground" data-testid="new-project-btn">
                    <Plus size={16} className="mr-2" /> {t('projects.new')}
                </Button>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="bg-surface-muted">
                    <TabsTrigger value="active" data-testid="projects-tab-active">Projets actifs</TabsTrigger>
                    {canManageTrash && (
                        <TabsTrigger value="trash" data-testid="projects-tab-trash">
                            <Trash2 size={13} className="mr-1.5" /> Corbeille
                            {trash.length > 0 && (
                                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1">
                                    {trash.length}
                                </span>
                            )}
                        </TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="active" className="mt-5">
                    <div className="card-soft overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-surface-muted text-left">
                                <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="px-6 py-4 font-medium">{t('projects.code')}</th>
                                    <th className="px-6 py-4 font-medium">{t('projects.name')}</th>
                                    <th className="px-6 py-4 font-medium">{t('projects.sector')}</th>
                                    <th className="px-6 py-4 font-medium">{t('projects.status')}</th>
                                    <th className="px-6 py-4 font-medium text-right">{t('projects.progress')}</th>
                                    <th className="px-6 py-4 font-medium text-right">{t('projects.budget')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {loading && (
                                    <tr><td colSpan={6} className="px-6 py-10 text-center text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /> {t('common.loading')}</td></tr>
                                )}
                                {!loading && projects.length === 0 && (
                                    <tr><td colSpan={6} className="px-6 py-12 text-center text-foreground/60" data-testid="empty-projects-table">{t('dashboard.no_projects')}</td></tr>
                                )}
                                {!loading && projects.map((p) => (
                                    <tr key={p.id} className="hover:bg-surface-muted transition-colors">
                                        <td className="px-6 py-4 font-mono text-xs">{p.code || p.id.slice(0, 8)}</td>
                                        <td className="px-6 py-4">
                                            <Link to={`/app/projects/${p.id}`} className="font-medium hover:text-primary transition-colors" data-testid={`project-row-${p.id}`}>{p.name}</Link>
                                        </td>
                                        <td className="px-6 py-4 text-foreground/80">{p.industry || '—'}</td>
                                        <td className="px-6 py-4"><span className="phase-badge bg-secondary-light text-secondary">{STATUS_LABEL[p.status] || p.status}</span></td>
                                        <td className="px-6 py-4 text-right font-mono">{p.progress || 0}%</td>
                                        <td className="px-6 py-4 text-right font-mono">{new Intl.NumberFormat('fr-FR').format(p.budget_total || 0)} {p.currency}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {canManageTrash && (
                    <TabsContent value="trash" className="mt-5">
                        <div className="card-soft p-5">
                            <div className="flex items-start gap-3 mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                <div>
                                    Les projets de la corbeille sont conservés <strong>30 jours</strong> puis purgés définitivement.
                                    Vous pouvez les restaurer ou les supprimer dès maintenant.
                                </div>
                            </div>
                            {loadingTrash && (
                                <div className="text-center py-10 text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /> Chargement…</div>
                            )}
                            {!loadingTrash && trash.length === 0 && (
                                <div className="text-center py-12 text-foreground/60" data-testid="empty-trash">La corbeille est vide.</div>
                            )}
                            {!loadingTrash && trash.length > 0 && (
                                <div className="divide-y divide-border" data-testid="trash-list">
                                    {trash.map(p => (
                                        <div key={p.id} className="py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`trash-row-${p.id}`}>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <div className="font-medium">{p.name}</div>
                                                    {p.code && <span className="font-mono text-xs text-muted-foreground">· {p.code}</span>}
                                                    {p.industry && <span className="phase-badge bg-surface-muted text-muted-foreground">{p.industry}</span>}
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    Supprimé le {new Date(p.deleted_at).toLocaleString('fr-FR')} ·
                                                    Purge automatique dans <strong className={p.days_left <= 3 ? 'text-destructive' : ''}>{p.days_left} jour{p.days_left > 1 ? 's' : ''}</strong>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button variant="outline" size="sm" onClick={() => restoreProject(p)} data-testid={`restore-${p.id}`}>
                                                    <Undo2 size={13} className="mr-1.5" /> Restaurer
                                                </Button>
                                                <Button variant="ghost" size="sm"
                                                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                                    onClick={() => purgeProject(p)} data-testid={`purge-${p.id}`}>
                                                    <Trash2 size={13} className="mr-1.5" /> Supprimer définitivement
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabsContent>
                )}
            </Tabs>

            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearchParams({}); }}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">{t('projects.create_title')}</DialogTitle>
                        <DialogDescription>{t('projects.create_subtitle')}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={create} className="space-y-4" data-testid="create-project-form">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2"><Label>{t('projects.field_name')}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-name" /></div>
                            <div className="space-y-2"><Label>{t('projects.field_code')}</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="create-code" /></div>
                            <div className="space-y-2">
                                <Label>{t('projects.field_industry')}</Label>
                                <select value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="create-industry">
                                    {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2"><Label>{t('projects.field_country')}</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="create-country" /></div>
                            <div className="space-y-2"><Label>{t('projects.field_budget')}</Label><Input type="number" value={form.budget_total} onChange={(e) => setForm({ ...form, budget_total: e.target.value })} data-testid="create-budget" /></div>
                            <div className="space-y-2"><Label>{t('projects.field_currency')}</Label>
                                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="create-currency">
                                    <option>XOF</option><option>XAF</option><option>EUR</option><option>USD</option>
                                </select>
                            </div>
                            <div className="space-y-2"><Label>{t('projects.field_start')}</Label><DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="create-start" /></div>
                            <div className="space-y-2"><Label>{t('projects.field_end')}</Label><DatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="create-end" /></div>
                        </div>
                        <div className="space-y-2"><Label>{t('projects.field_description')}</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="create-description" /></div>
                        {error && <div className="text-sm text-destructive">{error}</div>}
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
                            <Button type="submit" disabled={creating} className="bg-primary hover:bg-primary-hover" data-testid="create-submit">
                                {creating ? <Loader2 className="animate-spin" size={14} /> : t('projects.create_submit')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
